import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { fmtDate } from '../../shared/dates';

// Render an ISO UTC timestamp in the clinic's local timezone. The Notifications
// table stored `n.createdAt` as Postgres `toISOString()` — UTC — and slicing
// the string surfaced UTC hours next to a UTC date, which is wrong for anyone
// reading "when did this alert fire?" in Eastern time.
function fmtClinicLocal(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Outbound notifications log. Operators land here to see which case managers
 * have been (or would have been) texted about a missed appointment today.
 * The "Run scan now" button triggers the same logic the cron runs every 5 min
 * — useful when an admin marks a client as no-show mid-day and wants the alert
 * to fire immediately rather than waiting for the next tick.
 */
export function NotificationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/notifications', { params: { limit: 100 } }).then((r) => r.data),
    refetchInterval: 30_000,
  });
  // Read clinic timezone from settings so per-row timestamps render in the
  // operator's wall-clock instead of UTC. Falls back to America/New_York.
  const { data: clinicInfo } = useQuery({
    queryKey: ['clinic-info'],
    queryFn: () => api.get('/settings/clinic_info').then((r) => r.data),
  });
  const tz = (clinicInfo as any)?.timezone || 'America/New_York';

  const scan = useMutation({
    mutationFn: () => api.post('/notifications/missed-appt/scan').then((r) => r.data),
    onSuccess: (r) => {
      toast.success(
        `Scan complete — created ${r.notificationsCreated} (sent ${r.smsSent}, dry-run ${r.dryRun}, failed ${r.failed})`,
      );
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Scan failed'),
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <p className="text-sm text-slate-500">
            Outbound SMS alerts sent to case managers about missed client appointments. One per
            (case manager × client × day) — like a single morning attendance call from a school.
          </p>
        </div>
        <button
          className="btn-primary"
          disabled={scan.isPending}
          onClick={() => scan.mutate()}
        >
          {scan.isPending ? 'Scanning…' : 'Run missed-appt scan now'}
        </button>
      </header>

      <section className="card overflow-hidden">
        {isLoading ? (
          <p className="p-4 text-sm text-ink-subtle">Loading…</p>
        ) : (data ?? []).length === 0 ? (
          <p className="p-6 text-center text-sm text-ink-subtle">
            No notifications yet. Mark a client as no-show today, or run a scan.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Send date</th>
                <th className="px-3 py-2 text-left">Case manager</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Body</th>
              </tr>
            </thead>
            <tbody>
              {(data as any[]).map((n) => (
                <tr key={n.id} className="border-t border-line-subtle">
                  <td className="px-3 py-2 font-mono text-2xs text-ink-subtle">
                    {fmtClinicLocal(n.createdAt, tz)}
                  </td>
                  <td className="px-3 py-2 font-mono text-2xs">{fmtDate(n.sendDate.slice(0, 10))}</td>
                  <td className="px-3 py-2">{n.recipientName}</td>
                  <td className="px-3 py-2 font-mono text-2xs">{n.recipientPhone}</td>
                  <td className="px-3 py-2">{n.clientName}</td>
                  <td className="px-3 py-2">
                    <span className={statusCls(n.status)}>{n.status}</span>
                    {n.errorMessage && (
                      <div
                        className="mt-0.5 truncate text-2xs text-rose-600"
                        title={n.errorMessage}
                      >
                        {n.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="max-w-md truncate px-3 py-2 text-xs text-ink-subtle" title={n.body}>
                    {n.body}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function statusCls(s: string): string {
  switch (s) {
    case 'SENT':
      return 'pill-success';
    case 'DRY_RUN':
      return 'pill-info';
    case 'FAILED':
      return 'pill-danger';
    case 'PENDING':
    default:
      return 'pill-muted';
  }
}
